import apiClient from '../apiClient';

const LOCAL_PCF_RUN_TIMEOUT_MS = 30_000;

/** Stable machine-readable code the server returns (HTTP 403) when the run
 * does not fit into the license's remaining export elements. */
export const PCF_EXPORT_QUOTA_EXHAUSTED_CODE = 'export_element_limit_exhausted';

export type PcfRunGrouping = 'BySystemTypeName' | 'BySystemName' | 'ByParameter';

export interface RecordLocalPcfRunInput {
  readonly runId: string;
  readonly grouping: PcfRunGrouping;
  readonly elementCount: number;
  readonly filesProduced: number;
  readonly groupFailuresCount: number;
  readonly durationMs: number;
  readonly pipelineVersion: string;
  readonly pluginVersion?: string;
  readonly traceId: string;
  readonly snapshotCacheHit: boolean;
  readonly errorStage?: string;
  readonly traceparent: string;
}

export type RecordLocalPcfRunResult =
  | {
      readonly accepted: true;
      readonly runId: string;
      readonly isSuccess: boolean;
      readonly remainingExportElements: number | null;
      readonly isDemo: boolean;
    }
  | {
      readonly accepted: false;
      readonly error: string;
      readonly code?: string;
      readonly remainingExportElements?: number | null;
      readonly isDemo?: boolean;
    };

interface LocalPcfRunRequestBody {
  readonly runId: string;
  readonly grouping: PcfRunGrouping;
  readonly elementCount: number;
  readonly filesProduced: number;
  readonly groupFailuresCount: number;
  readonly durationMs: number;
  readonly pipelineVersion: string;
  readonly pluginVersion?: string;
  readonly traceId: string;
  readonly snapshotCacheHit: boolean;
  readonly errorStage?: string;
}

interface ApiErrorDetails {
  readonly message: string;
  readonly code?: string;
  readonly isDemo?: boolean;
  readonly remainingExportElements?: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseRemainingExportElements(value: unknown): number | null {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }
  throw new Error('remainingExportElements must be null or a non-negative integer');
}

function parseReceipt(
  value: unknown,
  expectedRunId: string,
): Extract<RecordLocalPcfRunResult, { accepted: true }> {
  if (!isRecord(value)
    || value.runId !== expectedRunId
    || typeof value.isSuccess !== 'boolean'
    || typeof value.isDemo !== 'boolean') {
    throw new Error('Invalid local PCF run receipt: response fields were malformed');
  }
  let remainingExportElements: number | null;
  try {
    remainingExportElements = parseRemainingExportElements(value.remainingExportElements);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'quota was malformed';
    throw new Error(`Invalid local PCF run receipt: ${detail}`);
  }
  return {
    accepted: true,
    runId: value.runId,
    isSuccess: value.isSuccess,
    remainingExportElements,
    isDemo: value.isDemo,
  };
}

function optionalQuota(value: unknown): number | null | undefined {
  try {
    return value === undefined ? undefined : parseRemainingExportElements(value);
  } catch {
    return undefined;
  }
}

function readApiError(error: unknown): ApiErrorDetails {
  if (!isRecord(error)) {
    return { message: 'Local PCF run receipt failed' };
  }
  const response = isRecord(error.response) ? error.response : undefined;
  const status = typeof response?.status === 'number' ? response.status : undefined;
  const data = isRecord(response?.data) ? response.data : undefined;
  const code = typeof data?.code === 'string' ? data.code : undefined;
  const isDemo = typeof data?.isDemo === 'boolean' ? data.isDemo : undefined;
  const remainingExportElements = optionalQuota(data?.remainingExportElements);
  const serverMessage = typeof data?.message === 'string'
    ? data.message
    : typeof data?.error === 'string'
      ? data.error
      : undefined;

  if (code === PCF_EXPORT_QUOTA_EXHAUSTED_CODE) {
    return {
      message: serverMessage || 'The export element limit has been reached for this license.',
      code,
      isDemo,
      remainingExportElements: remainingExportElements ?? 0,
    };
  }
  if (serverMessage) {
    return { message: serverMessage, code, isDemo, remainingExportElements };
  }
  if (status === 401 || status === 403) {
    return {
      message: 'Please sign in again — your session has expired.',
      code,
      isDemo,
      remainingExportElements,
    };
  }
  if (status) {
    return {
      message: `Run recording failed with HTTP ${status}.`,
      code,
      isDemo,
      remainingExportElements,
    };
  }
  return {
    message: typeof error.message === 'string'
      ? error.message
      : 'Local PCF run receipt failed',
    code,
    isDemo,
    remainingExportElements,
  };
}

function buildRequestBody(input: RecordLocalPcfRunInput): LocalPcfRunRequestBody {
  return {
    runId: input.runId,
    grouping: input.grouping,
    elementCount: input.elementCount,
    filesProduced: input.filesProduced,
    groupFailuresCount: input.groupFailuresCount,
    durationMs: input.durationMs,
    pipelineVersion: input.pipelineVersion,
    ...(input.pluginVersion ? { pluginVersion: input.pluginVersion } : {}),
    traceId: input.traceId,
    snapshotCacheHit: input.snapshotCacheHit,
    ...(input.errorStage ? { errorStage: input.errorStage } : {}),
  };
}

export async function recordLocalPcfRun(
  input: RecordLocalPcfRunInput,
): Promise<RecordLocalPcfRunResult> {
  try {
    const response = await apiClient.post<unknown>(
      '/pcf-runs/local',
      buildRequestBody(input),
      {
        timeout: LOCAL_PCF_RUN_TIMEOUT_MS,
        headers: {
          'X-Trace-Id': input.traceId,
          traceparent: input.traceparent,
        },
      },
    );
    return parseReceipt(response.data, input.runId);
  } catch (error) {
    const details = readApiError(error);
    return {
      accepted: false,
      error: details.message,
      code: details.code,
      remainingExportElements: details.remainingExportElements,
      isDemo: details.isDemo,
    };
  }
}
